<template>
  <DropdownBasic
    :id="id"
    v-model="value"
    :name="name"
    :label="label"
    :aria-label="ariaLabel"
    :options="options"
    :disabled="disabled"
    :disabled-message="disabledMessage"
    :disabled-option="disabledOption"
    :label-size="labelSize"
    :dropdown-size="dropdownSize"
    :has-error="hasError"
    @update:modelValue="updatedModelValue"
    @change="changedModelValue"
  />
  <div
    v-show="errorMessage && required"
    aria-live="polite"
    aria-atomic="true"
    :class="['form-error', {'is-visible': errorMessage}]"
  >
    <Icon
      role="graphics-symbol"
      :icon="csgErrorRed"
      :aria-label="errorMessage ? 'Error':''"
      :aria-hidden="!errorMessage && !required"
    />
    <span> {{ errorMessage }} </span>
  </div>
</template>

<script setup>
import { useField } from 'vee-validate';
import { computed, watch } from 'vue';
import { csgErrorRed } from '@csgSvgIcons/icons';
import Icon from '../icon';
import generateRandomId from '../../utils/randomNumber';
import DropdownBasic from '../dropdownBasic/DropdownBasic';

const props = defineProps({
  id: {
    type: [String, Number],
    default: () => generateRandomId(),
  },
  name: {
    type: String,
    default: 'dropdown',
  },
  label: {
    type: String,
    default: 'Dropdown',
  },
  options: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  labelSize: {
    type: String,
    default: 'default',
  },
  validationMessage: {
    type: String,
    default: '',
  },
  dropdownSize: {
    type: String,
    default: 'large',
  },
  disabledOption: {
    type: Boolean,
    default: false,
  },
  disabledMessage: {
    type: String,
    default: 'Select an option',
  },
  required: {
    type: Boolean,
    default: true,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const {
  value, errorMessage, meta, setTouched, validate,
} = useField(
  props.name,
  (selectedValue) => {
    if (selectedValue !== '') {
      return true;
    }
    return props.validationMessage || `${props.name} is required.`;
  }, {
    initialValue: props.modelValue,
  },
);

const hasError = computed(() => props.required && (meta && !meta.valid && (meta.touched || meta.dirty)));
const updatedModelValue = (updatedValue) => {
  setTouched(true);
  validate();
  emit('update:modelValue', updatedValue);
};
const changedModelValue = (e) => {
  emit('change', e);
};

watch(() => props.modelValue, (newValue) => {
  value.value = newValue;
});
</script>
<style lang="scss" scoped>
@import "~@core/csg/src/scss/forms/forms.scss";
@include csg-forms;
</style>
